import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Bar, Line } from 'react-chartjs-2';
import StatCard from '../components/StatCard';
import MiniStatCard from '../components/MiniStatCard';
import CardSkeleton, { MiniCardSkeleton } from '../components/CardSkeleton';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import currency from 'helpers/currency';
import { DollarSign, TrendingUp, RotateCcw, Percent } from 'react-feather';
import dashboardStatistics from 'api/requests/dashboardStatistics';

export default function VendasTab({ periodParams }) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!periodParams) return;

        setIsLoading(true);
        dashboardStatistics.getVendas(periodParams)
            .then(response => setData(response.data))
            .catch(error => console.error('Erro ao carregar vendas:', error))
            .finally(() => setIsLoading(false));
    }, [periodParams]);

    if (isLoading) {
        return (
            <Container>
                <KpiGrid>
                    <MiniCardSkeleton />
                    <MiniCardSkeleton />
                    <MiniCardSkeleton />
                    <MiniCardSkeleton />
                </KpiGrid>
                <ChartsGrid>
                    <CardSkeleton height={340} />
                    <CardSkeleton height={340} />
                </ChartsGrid>
            </Container>
        );
    }

    if (!data) return null;

    const { ticketMedio, ticketMedioTrend, totalVendas, totalVendasTrend, totalEstornos, totalEstornosTrend, taxaConversao, taxaConversaoTrend, vendasPorMes, vendasPorDia } = data;

    const vendasMesChartData = {
        labels: vendasPorMes.labels,
        datasets: [
            {
                label: 'Vendas',
                data: vendasPorMes.valores,
                backgroundColor: 'rgba(128, 37, 232, 0.7)',
                borderColor: '#8025e8',
                borderWidth: 1,
                borderRadius: 6,
                barPercentage: 0.6,
            },
            {
                label: 'Estornos',
                data: vendasPorMes.estornos,
                backgroundColor: 'rgba(231, 80, 90, 0.7)',
                borderColor: '#E7505A',
                borderWidth: 1,
                borderRadius: 6,
                barPercentage: 0.6,
            },
        ],
    };

    const vendasMesOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            yAxes: [{
                ticks: {
                    beginAtZero: true,
                    fontColor: '#999',
                    fontSize: 12,
                    callback: function (value) {
                        if (value >= 1000) return (value / 1000) + 'k';
                        return value;
                    },
                },
                gridLines: { color: '#f0f0f0', drawBorder: false },
            }],
            xAxes: [{
                ticks: { fontColor: '#999', fontSize: 12 },
                gridLines: { display: false },
            }],
        },
        legend: {
            position: 'top',
            align: 'end',
            labels: { boxWidth: 12, fontSize: 12 },
        },
        tooltips: {
            backgroundColor: '#1A1A1A',
            cornerRadius: 8,
            xPadding: 12,
            yPadding: 10,
            callbacks: {
                label: function (tooltipItem, data) {
                    return ` ${data.datasets[tooltipItem.datasetIndex].label}: ${currency.format(tooltipItem.yLabel)}`;
                },
            },
        },
    };

    const vendasDiaChartData = {
        labels: vendasPorDia.labels,
        datasets: [
            {
                label: 'Vendas por dia',
                data: vendasPorDia.valores,
                backgroundColor: 'rgba(79, 134, 247, 0.15)',
                borderColor: '#4F86F7',
                borderWidth: 2,
                fill: true,
                tension: 0.4,
                pointRadius: 4,
                pointBackgroundColor: '#4F86F7',
                pointHoverRadius: 6,
            },
        ],
    };

    const vendasDiaOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            yAxes: [{
                ticks: {
                    beginAtZero: true,
                    fontColor: '#999',
                    fontSize: 12,
                    callback: function (value) {
                        if (value >= 1000) return (value / 1000) + 'k';
                        return value;
                    },
                },
                gridLines: { color: '#f0f0f0', drawBorder: false },
            }],
            xAxes: [{
                ticks: { fontColor: '#999', fontSize: 12 },
                gridLines: { display: false },
            }],
        },
        legend: { display: false },
        tooltips: {
            backgroundColor: '#1A1A1A',
            cornerRadius: 8,
            callbacks: {
                label: function (tooltipItem) {
                    return ` ${currency.format(tooltipItem.yLabel)}`;
                },
            },
        },
    };

    return (
        <Container>
            <KpiGrid>
                <MiniStatCard
                    label="Ticket Medio"
                    value={currency.format(ticketMedio)}
                    trend={ticketMedioTrend}
                    isPositive
                    icon={DollarSign}
                />
                <MiniStatCard
                    label="Valor Total de Vendas"
                    value={currency.format(totalVendas)}
                    trend={totalVendasTrend}
                    isPositive
                    icon={TrendingUp}
                />
                <MiniStatCard
                    label="Total de Estornos"
                    value={currency.format(totalEstornos)}
                    trend={totalEstornosTrend}
                    isPositive={false}
                    icon={RotateCcw}
                />
                <MiniStatCard
                    label="Taxa de Conversao"
                    value={`${taxaConversao}%`}
                    trend={taxaConversaoTrend}
                    isPositive
                    icon={Percent}
                />
            </KpiGrid>

            <ChartsGrid>
                <StatCard title="Vendas vs Estornos" subtitle="Comparativo mensal de vendas e estornos">
                    <ChartContainer>
                        <Bar data={vendasMesChartData} options={vendasMesOptions} />
                    </ChartContainer>
                </StatCard>

                <StatCard title="Vendas por Dia da Semana" subtitle="Distribuicao de vendas durante a semana">
                    <ChartContainer>
                        <Line data={vendasDiaChartData} options={vendasDiaOptions} />
                    </ChartContainer>
                </StatCard>
            </ChartsGrid>
        </Container>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const KpiGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;

    @media screen and (max-width: ${breakpoints.lg}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (max-width: ${breakpoints.sm}) {
        grid-template-columns: 1fr;
    }
`;

const ChartsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;

    @media screen and (max-width: ${breakpoints.md}) {
        grid-template-columns: 1fr;
    }
`;

const ChartContainer = styled.div`
    height: 280px;
    width: 100%;
`;
